import { useI18n } from "@/context/I18nContext";

import { useEffect, useState } from "react";
import { loadPdfjs } from "@/lib/pdfLazy";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { SignaturePad } from "@/components/SignaturePad";
import { generateQuotePdf } from "@/lib/pdfLazy";
import { CGV_TITLE, CGV_SECTIONS, CGV_FOOTER } from "@/lib/cgv";
import { t as tr, loadDict } from "@/lib/i18n";
import { Loader2, Check, Mail, Link2, PenLine, Camera } from "lucide-react";
import { PaperOrderPanel } from "@/components/PaperOrderPanel";


// Full-screen signing view: the real quote PDF (with CGV) rendered page by page, CGV acceptance + signature on the right.
export const SignView = ({ open, onOpenChange, quote, confirmLabel, onConfirm, busy, testid = "form-sign-order", onEditClient, onEditQuote, onSendLink, onCopyLink, remote = false, onPaperConfirm }) => {
  const { t } = useI18n();
  const cgvLang = quote?.lang || "fr";
  const [, setDictReady] = useState(0);
  useEffect(() => { loadDict(cgvLang).then(() => setDictReady((v) => v + 1)).catch(() => {}); }, [cgvLang]);
  const ct = (s) => tr(s, cgvLang);
  const [pages, setPages] = useState([]);
  const [rendering, setRendering] = useState(false);
  const [sig, setSig] = useState("");
  const [cgv, setCgv] = useState(false);
  const [email, setEmail] = useState("");
  const [mode, setMode] = useState("pad");
  const emailOk = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());

  useEffect(() => {
    if (!open || !quote) return;
    setSig(""); setCgv(false); setEmail(quote.client?.email || ""); setMode("pad");
    let cancelled = false;
    setRendering(true); setPages([]);
    (async () => {
      try {
        const data = await generateQuotePdf(quote, { arrayBuffer: true, noCgv: true });
        const pdfjsLib = await loadPdfjs();
        const pdf = await pdfjsLib.getDocument({ data }).promise;
        const out = [];
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const vp = page.getViewport({ scale: 2 });
          const c = document.createElement("canvas");
          c.width = vp.width; c.height = vp.height;
          await page.render({ canvasContext: c.getContext("2d"), viewport: vp }).promise;
          out.push(c.toDataURL("image/jpeg", 0.85));
        }
        if (!cancelled) setPages(out);
      } finally { if (!cancelled) setRendering(false); }
    })();
    return () => { cancelled = true; };
  }, [open, quote]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[98vw] max-w-[1000px] sm:max-w-[1000px] h-[96vh] p-0 overflow-hidden flex flex-col gap-0" data-testid="sign-view">
        <div className="px-5 py-3 pr-12 border-b border-border shrink-0 flex flex-wrap items-center justify-between gap-2">
          <div>
            <DialogTitle>{t("Récapitulatif du devis")}</DialogTitle>
            <DialogDescription className="mt-1">
              {t("Devis")} {quote?.quote_number}{quote?.client?.name ? ` · ${quote.client.name}` : ""}. {t("Lisez le devis complet, puis signez en bas de page.")}
            </DialogDescription>
          </div>
          <div className="flex flex-wrap gap-2">
            {onEditQuote && <Button size="sm" variant="outline" onClick={onEditQuote} data-testid="sign-edit-quote-top">{t("Modifier le devis")}</Button>}
            {onEditClient && <Button size="sm" variant="outline" onClick={onEditClient} data-testid="sign-edit-client-top">{t("Modifier les coordonnées client")}</Button>}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto bg-muted" data-testid="sign-view-scroll">
        <div className="p-2 sm:p-4 space-y-4" data-testid="sign-view-pdf">
          {rendering && <div className="flex items-center justify-center h-[60vh] text-muted-foreground gap-2"><Loader2 className="w-5 h-5 animate-spin" /> {t("Préparation du devis…")}</div>}
          {pages.map((src, i) => (
            <img key={i} src={src} alt={`Page ${i + 1}`} className="w-full block bg-white shadow-md" data-testid={`sign-view-page-${i + 1}`} />
          ))}
          {pages.length > 0 && (
            <div className="bg-white shadow-md p-5 sm:p-8 text-[13px] leading-relaxed text-neutral-700" data-testid="sign-view-cgv">
              <h3 className="text-center font-semibold text-base text-neutral-900 mb-4">{ct(CGV_TITLE)}</h3>
              <div className="sm:columns-2 gap-8">
                {CGV_SECTIONS.map(([h, body]) => (
                  <div key={h} className="mb-3 break-inside-avoid-column">
                    <div className="font-semibold text-neutral-900">{ct(h)}</div>
                    {ct(body).split("\n").map((p, i) => <p key={i} className="mt-1">{p}</p>)}
                  </div>
                ))}
              </div>
              <div className="text-center text-[11px] text-neutral-500 mt-4">{ct(CGV_FOOTER)}</div>
            </div>
          )}
        </div>
        <div className="bg-background border-t border-border p-5 sm:p-8 flex flex-col gap-5" data-testid="sign-view-signatures">
          <div className="text-lg font-semibold">{t("Bon pour accord")}</div>
          {onPaperConfirm && (
            <div className="inline-flex self-start rounded-full border border-border p-1 gap-1" data-testid="sign-mode">
              <Button size="sm" variant={mode === "pad" ? "default" : "ghost"} className="rounded-full gap-1.5" onClick={() => setMode("pad")} data-testid="sign-mode-pad"><PenLine className="w-4 h-4" /> {t("Signature sur l'écran")}</Button>
              <Button size="sm" variant={mode === "paper" ? "default" : "ghost"} className="rounded-full gap-1.5" onClick={() => setMode("paper")} data-testid="sign-mode-paper"><Camera className="w-4 h-4" /> {t("Bon de commande papier signé")}</Button>
            </div>
          )}
          {mode === "paper" && onPaperConfirm ? <PaperOrderPanel onConfirm={onPaperConfirm} busy={busy} /> : <>
          <label className="flex items-start gap-2 text-sm cursor-pointer" data-testid="cgv-accept-label">
            <Checkbox checked={cgv} onCheckedChange={(v) => setCgv(!!v)} data-testid="cgv-accept" className="mt-0.5" />
            <span>{t("J'ai lu et j'accepte les conditions générales de vente")}</span>
          </label>
          <div>
            <div className="text-sm font-medium mb-1.5">{t("Signature du client — Bon pour accord")}</div>
            <SignaturePad value={sig} onChange={setSig} />
          </div>
          {!remote && <div data-testid="sign-email-block">
            <div className="text-sm font-medium mb-1.5">{t("E-mail du client")} <span className="text-destructive">*</span></div>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="client@exemple.fr" className="h-11" data-testid="sign-client-email" />
            <div className={`text-xs mt-1.5 ${emailOk ? "text-muted-foreground" : "text-destructive"}`} data-testid="sign-email-hint">
              {emailOk ? `Une copie du devis signé (avec les CGV) sera envoyée à ${email.trim()}` : "E-mail obligatoire : le client recevra une copie du devis signé avec les CGV."}
            </div>
          </div>}
          <div className="flex flex-col sm:flex-row-reverse sm:flex-wrap gap-2 pt-2" data-testid="sign-view-actions">
            <Button size="lg" onClick={() => onConfirm(sig, "", email.trim())} disabled={busy || !sig || !cgv || !emailOk} data-testid={testid} className="gap-2 sm:basis-full">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} {confirmLabel}
            </Button>
            {onSendLink && <Button size="lg" variant="outline" onClick={() => onSendLink(email.trim())} disabled={busy || !emailOk} data-testid="sign-send-link" className="gap-2"><Mail className="w-4 h-4" /> {t("Envoyer par e-mail au client")}</Button>}
            {onCopyLink && <Button size="lg" variant="outline" onClick={onCopyLink} disabled={busy} data-testid="sign-copy-link" className="gap-2"><Link2 className="w-4 h-4" /> {t("Copier le lien vers le devis")}</Button>}
            {onEditQuote && <Button size="lg" variant="outline" onClick={onEditQuote} data-testid="sign-edit-quote">{t("Modifier le devis")}</Button>}
            {onEditClient && <Button size="lg" variant="outline" onClick={onEditClient} data-testid="sign-edit-client">{t("Modifier les coordonnées client")}</Button>}
          </div>
          </>}
        </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
